import React, { Component, ReactNode } from 'react';
import { AlertCircle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '@/components/ui/alert';
import i18n from '@/i18n';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log error to console in development
    if (import.meta.env.DEV) {
      console.error('[ErrorBoundary] Caught error:', error);
      console.error('[ErrorBoundary] Error info:', errorInfo);
    }

    // Call optional error handler
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    this.setState({
      error,
      errorInfo,
    });

    // In production, you could send error to error reporting service here
    // e.g., Sentry.captureException(error, { contexts: { react: errorInfo } });
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render() {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <div className="min-h-screen flex items-center justify-center bg-background p-4">
          <div className="max-w-2xl w-full">
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle className="text-lg font-semibold mb-2">
                {i18n.t('errorBoundary.title')}
              </AlertTitle>
              <AlertDescription className="space-y-3">
                <p>{i18n.t('errorBoundary.description')}</p>

                {import.meta.env.DEV && this.state.error && (
                  <details className="mt-4">
                    <summary className="cursor-pointer text-sm font-medium mb-2">
                      {i18n.t('errorBoundary.detailsLabel')}
                    </summary>
                    <div className="mt-2 p-3 bg-background rounded border text-xs font-mono overflow-auto max-h-64">
                      <div className="mb-2">
                        <strong>{i18n.t('errorBoundary.errorMessageLabel')}</strong>
                        <div className="mt-1 text-red-600">
                          {this.state.error.message}
                        </div>
                      </div>
                      {this.state.error.stack && (
                        <div className="mt-2">
                          <strong>{i18n.t('errorBoundary.stackLabel')}</strong>
                          <pre className="mt-1 text-xs whitespace-pre-wrap">
                            {this.state.error.stack}
                          </pre>
                        </div>
                      )}
                    </div>
                  </details>
                )}

                <div className="flex gap-2 mt-4">
                  <Button
                    onClick={this.handleReset}
                    variant="outline"
                    size="sm"
                  >
                    <RefreshCw className="mr-2 h-4 w-4" />
                    {i18n.t('errorBoundary.retry')}
                  </Button>
                  <Button
                    onClick={() => {
                      window.location.href = '/crm';
                    }}
                    variant="default"
                    size="sm"
                  >
                    {i18n.t('errorBoundary.home')}
                  </Button>
                </div>
              </AlertDescription>
            </Alert>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
